import { useEffect, useState } from "react";
import { Box, ListItemButton, Stack, Typography } from "@mui/material";
import { TourTileIcon } from "./TourIcons";
import tourColors from "../styles/tour.colors";
import tourTokens from "../styles/tour.tokens";
import { resolveTargetElement } from "../runtime/target-resolver";
import useTour from "../hooks/use-tour";
import type { TourDefinition } from "../types/tour.types";

const c = tourColors;
const t = tourTokens.launcher;

const NO_TOURS: TourDefinition[] = [];

const isRunnableNow = (tour: TourDefinition): boolean =>
  !tour.autoStartWhen || Boolean(resolveTargetElement(tour.autoStartWhen));

interface TourLauncherProps {
  onLaunch?: () => void;
}

const TourLauncher = ({ onLaunch }: TourLauncherProps) => {
  const tour = useTour();
  const availableTours = tour?.availableTours ?? NO_TOURS;
  const [runnable, setRunnable] = useState<TourDefinition[]>(NO_TOURS);

  useEffect(() => {
    setRunnable(availableTours.filter(isRunnableNow));
  }, [availableTours]);

  if (!tour || runnable.length === 0) return null;

  const launch = (target: TourDefinition) => {
    onLaunch?.();
    if (tour.isTourCompleted(target.id)) {
      void tour.restartTour(target.id);
      return;
    }
    void tour.startTour(target.id);
  };

  return (
    <>
      {runnable.map((target) => (
        <ListItemButton
          key={target.id}
          onClick={() => launch(target)}
          sx={{
            px: t.paddingX,
            py: t.paddingY,
            gap: t.gap,
            alignItems: "center",
            "&:hover": { backgroundColor: c.launcher.hoverBg },
          }}
        >
          <Box
            sx={{
              width: t.tileSize,
              height: t.tileSize,
              borderRadius: t.tileRadius,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: c.launcher.tileBg,
              color: c.launcher.tileFg,
            }}
          >
            <TourTileIcon sx={{ fontSize: t.iconSize }} />
          </Box>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: t.titleFontSize,
                fontWeight: t.titleFontWeight,
                lineHeight: t.titleLineHeight,
                color: c.launcher.title,
              }}
            >
              {target.name}
            </Typography>
            <Typography
              sx={{
                fontSize: t.descriptionFontSize,
                fontWeight: t.descriptionFontWeight,
                color: c.launcher.description,
              }}
            >
              {target.description}
            </Typography>
          </Stack>
        </ListItemButton>
      ))}
    </>
  );
};

export default TourLauncher;
